import React, { FC, useState, useEffect, useCallback, ReactNode, forwardRef, Ref } from 'react';
import { useLocation, Redirect } from 'react-router-dom';
import moment from 'moment';
import AppBar from '@fenderdigital/ui/AppBar';
import { Playlist } from '@fenderdigital/api-definitions/play';
import isDomUsable from '@fenderdigital/utils/dom';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { pathContainsMatch } from '../../utils/navigation-utils';
import Navigation from '../Navigation';
import Link from '../Link';
import StreaksButton, { StreaksButtonTooltipHandle } from '../StreaksButton';
import NavSearch from '../NavSearch';
import useLocalStorage from '../../hooks/useLocalStorage';
import PersistentUpgrade from '../PersistentUpgradeHeader';
// eslint-disable-next-line max-len
import { PERSISTENT_UPGRADE_BANNER_HEIGHT } from '../PersistentUpgradeHeader/persistent-upgrade-styles';
import useDisableBodyScroll from '../../hooks/useDisableBodyScroll';
import { getFlag } from '../../lib/feature-flags';
import * as S from './SubscribedLayout-styled';
import PersistentUpgradeBanner from '../PersistentUpgradeBanner';

export const APP_BAR_HEIGHT = '3.75rem';

function getTopPadding({
  isAppBarFixed,
  isAppBarHidden,
  isBannerVisible,
}: {
  isAppBarFixed: boolean;
  isAppBarHidden: boolean;
  isBannerVisible: boolean;
}): string {
  if (isAppBarFixed || isAppBarHidden) {
    if (isBannerVisible) {
      return PERSISTENT_UPGRADE_BANNER_HEIGHT;
    }
    return '0px';
  }
  if (isBannerVisible) {
    return `calc(${PERSISTENT_UPGRADE_BANNER_HEIGHT} + ${APP_BAR_HEIGHT})`;
  }
  return APP_BAR_HEIGHT;
}

function getBottomPadding({
  isFreemiumBannerVisible,
  shouldAddPadding,
}: {
  isFreemiumBannerVisible: boolean;
  shouldAddPadding: boolean;
}): string {
  if (shouldAddPadding) {
    if (isFreemiumBannerVisible) {
      return 'calc(3rem + 56px)';
    }
    return '56px';
  }
  return '0px';
}

interface SubscribedLayoutContentProps {
  children?: ReactNode;
  isMenuVisible: boolean;
  handleOpenMenu: () => void;
  handleCloseMenu: () => void;
  playlist?: Playlist[];
  ref?: Ref<HTMLButtonElement>;
  streaksButtonRef: Ref<StreaksButtonTooltipHandle>;
}

const SubscribedLayoutContent: FC<SubscribedLayoutContentProps> = forwardRef(
  (
    { children, isMenuVisible, handleOpenMenu, handleCloseMenu, playlist, streaksButtonRef },
    ref,
  ) => {
    const location = useLocation();
    const { isFreeUser, subscriptionExpirationDate, accessLevel: userAccessLevels } = usePlayUser();

    // Feature Flags
    const isPlay5 = getFlag('isPlay5');

    // Disable Scroll
    const scrollDisabledPaths = ['practice'];
    const isScrollDisabled = pathContainsMatch(location.pathname, scrollDisabledPaths);
    useDisableBodyScroll(isScrollDisabled);

    // Footer
    const params = new URLSearchParams(location.search);
    const shouldHideNavFooter = params.get('hide-nav-footer') === 'true';

    // App Bar
    const hiddenAppBarRoutes = ['feedback-mode'];
    const isHiddenAppBarRoute = pathContainsMatch(location.pathname, hiddenAppBarRoutes);
    const isMobile = window.innerWidth < 450 || window.innerHeight < 450;
    const shouldHideAppBar = (isMobile && isHiddenAppBarRoute) || shouldHideNavFooter;

    // Welcome Mat
    const isHome = location.pathname === '/';
    const [isScrolled, setIsScrolled] = useState(false);

    const handleScroll = (): void => {
      const distanceY = window.pageYOffset || document.documentElement.scrollTop;
      const threshold = 50;
      if (distanceY > threshold) {
        setIsScrolled(true);
      } else if (distanceY < threshold) {
        setIsScrolled(false);
      }
    };

    // Freemium Banner
    const freemiumBannerRoutes = ['path', 'songs', 'skills', 'collections', 'search'];
    const isBasicUser = userAccessLevels?.includes('free');
    const showFreemiumBanner = !!(
      isPlay5 &&
      isBasicUser &&
      (isHome || pathContainsMatch(location.pathname, freemiumBannerRoutes))
    );

    // Last Chance
    const [hasSeenLastChanceOffer] = useLocalStorage('hasSeenLastChanceOffer', false);
    const [shouldShowLastChanceOffer, setShouldShowLastChanceOffer] = useState(false);

    // General
    const topPadding = getTopPadding({
      isAppBarFixed: isHome,
      isAppBarHidden: shouldHideAppBar,
      isBannerVisible: !showFreemiumBanner && isFreeUser && !isPlay5,
    });
    const bottomPadding = getBottomPadding({
      isFreemiumBannerVisible: showFreemiumBanner,
      shouldAddPadding: pathContainsMatch(location.pathname, ['path', 'songs', 'skills', 'search']),
    });

    const checkIfExpiringSoon = useCallback(
      expirationDate => {
        const expiresInLessThan4Days = moment.utc(expirationDate).diff(moment().utc(), 'days') < 4;

        if (expiresInLessThan4Days && !isPlay5) {
          setShouldShowLastChanceOffer(true);
        }
      },
      [setShouldShowLastChanceOffer],
    );

    useEffect(() => {
      if (isFreeUser && !hasSeenLastChanceOffer && subscriptionExpirationDate && !isPlay5) {
        checkIfExpiringSoon(subscriptionExpirationDate);
      }
    }, [
      checkIfExpiringSoon,
      hasSeenLastChanceOffer,
      isFreeUser,
      isPlay5,
      subscriptionExpirationDate,
    ]);

    useEffect(() => {
      if (isDomUsable()) {
        document.addEventListener('scroll', handleScroll, false);
      }
      return (): void => document.removeEventListener('scroll', handleScroll);
    }, []);

    if (shouldShowLastChanceOffer) {
      return <Redirect to="/plans/last-chance" />;
    }

    return (
      <div data-id="subscribed-layout-content">
        {!isPlay5 && !showFreemiumBanner && isFreeUser && (
          <PersistentUpgrade subscriptionExpirationDate={subscriptionExpirationDate} />
        )}
        <S.Layout
          hasAppBarScrollTransition={isHome}
          isScrolled={isScrolled}
          topPadding={topPadding}
        >
          {!shouldHideAppBar && (
            <AppBar
              dataId="app-bar"
              id="app-bar"
              appBarHeight={APP_BAR_HEIGHT}
              LinkComponent={Link}
              onMenuClick={handleOpenMenu}
              RightSideComponent={[
                <React.Fragment key="nav-bar">
                  <NavSearch isTransparent={isHome && !isScrolled} key="nav-search" />
                  <StreaksButton
                    isBackgroundTransparent={isHome && !isScrolled}
                    key="streaks"
                    ref={streaksButtonRef}
                  />
                </React.Fragment>,
              ]}
              logoUrl="/"
              topPosition={isFreeUser && !isPlay5 ? PERSISTENT_UPGRADE_BANNER_HEIGHT : '0'}
              ref={ref}
              hideNavHairline={isHome && !isScrolled}
            />
          )}
          <S.ContentWrapper>
            <S.Content bottomPadding={bottomPadding}>{children}</S.Content>
          </S.ContentWrapper>
          <Navigation
            isMenuVisible={isMenuVisible}
            playlist={playlist}
            handleCloseMenu={handleCloseMenu}
          />
        </S.Layout>
        {showFreemiumBanner && !isPlay5 && <PersistentUpgradeBanner />}
      </div>
    );
  },
);

export default SubscribedLayoutContent;
